import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  FileText, Edit3, Save, X, RefreshCw, Loader2, Clock, ChevronDown, ChevronRight, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VersionItem({ version, onSelect, isSelected }) {
  const date = version.created_at ? new Date(version.created_at).toLocaleDateString() : '';
  const time = version.created_at ? new Date(version.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <button
      onClick={() => onSelect(version)}
      className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-xs ${
        isSelected ? 'bg-stone-100 border border-stone-300' : 'hover:bg-stone-50 border border-transparent'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-stone-700">v{version.version}</span>
        <span className="text-stone-400">{date}</span>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
          version.change_type === 'manual_edit' ? 'bg-blue-50 text-blue-600' : 'bg-stone-100 text-stone-500'
        }`}>
          {version.change_type === 'manual_edit' ? 'Manual edit' : 'Auto-generated'}
        </span>
        <span className="text-[10px] text-stone-400">{version.word_count} words</span>
      </div>
    </button>
  );
}

export default function MdFilePanel({ type, entityId, secondaryEntityId }) {
  const { getAuthHeader } = useAuth();
  const [mdFile, setMdFile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [showVersions, setShowVersions] = useState(false);

  const fetchMdFile = useCallback(async () => {
    try {
      setLoading(true);
      const params = secondaryEntityId ? `?secondary_entity_id=${secondaryEntityId}` : '';
      const res = await axios.get(`${API}/md-files/${type}/${entityId}${params}`, { headers: getAuthHeader() });
      setMdFile(res.data);
      setEditContent(res.data.md_content || '');
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error('Failed to load AI context');
      }
    } finally {
      setLoading(false);
    }
  }, [type, entityId, secondaryEntityId, getAuthHeader]);

  const fetchVersions = useCallback(async () => {
    try {
      const params = secondaryEntityId ? `?secondary_entity_id=${secondaryEntityId}` : '';
      const res = await axios.get(`${API}/md-files/${type}/${entityId}/versions${params}`, { headers: getAuthHeader() });
      setVersions(res.data.versions || []);
    } catch {}
  }, [type, entityId, secondaryEntityId, getAuthHeader]);

  useEffect(() => {
    fetchMdFile();
  }, [fetchMdFile]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/md-files/${type}/${entityId}/regenerate`,
        secondaryEntityId ? { secondary_entity_id: secondaryEntityId } : {},
        { headers: getAuthHeader() }
      );
      setMdFile(prev => ({
        ...prev,
        md_content: res.data.md_content,
        version: res.data.version,
      }));
      setEditContent(res.data.md_content);
      toast.success('AI context regenerated');
    } catch (err) {
      toast.error('Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/md-files/${type}/${entityId}`,
        {
          md_content: editContent,
          secondary_entity_id: secondaryEntityId,
          change_summary: 'Manual edit from UI',
        },
        { headers: getAuthHeader() }
      );
      setMdFile(prev => ({ ...prev, md_content: editContent, version: (prev.version || 0) + 1 }));
      setEditMode(false);
      toast.success('Saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleViewVersion = async (version) => {
    try {
      const params = secondaryEntityId ? `?secondary_entity_id=${secondaryEntityId}` : '';
      const res = await axios.get(`${API}/md-files/${type}/${entityId}/versions/${version.version}${params}`, { headers: getAuthHeader() });
      setSelectedVersion(res.data);
    } catch {
      toast.error('Failed to load version');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
        <span className="ml-2 text-sm text-stone-400">Generating AI context...</span>
      </div>
    );
  }

  const typeLabels = {
    contact: 'Contact Context',
    account: 'Company Context',
    communication_pair: 'Communication History',
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-stone-900">{typeLabels[type] || 'AI Context'}</h3>
          {mdFile && (
            <span className="text-[10px] text-stone-400">v{mdFile.version} | {mdFile.metadata?.word_count || 0} words</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button size="sm" variant="outline" onClick={() => { setEditMode(false); setEditContent(mdFile?.md_content || ''); }} className="text-xs">
                <X className="w-3 h-3 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs bg-stone-800 hover:bg-stone-900 text-white">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => { setShowVersions(!showVersions); if (!showVersions) fetchVersions(); }} className="text-xs">
                <Clock className="w-3 h-3 mr-1" /> History
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="text-xs">
                <Edit3 className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={regenerating} className="text-xs">
                {regenerating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Regenerate
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-4">
        {/* Main content */}
        <div className="flex-1">
          {editMode ? (
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              rows={20}
              className="w-full px-4 py-3 text-xs font-mono border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 leading-relaxed"
            />
          ) : selectedVersion ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-stone-500">
                  Viewing version {selectedVersion.version} from {new Date(selectedVersion.created_at).toLocaleDateString()}
                </p>
                <Button size="sm" variant="outline" onClick={() => setSelectedVersion(null)} className="text-xs">
                  <Eye className="w-3 h-3 mr-1" /> Current
                </Button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <pre className="text-xs text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
                  {selectedVersion.md_content}
                </pre>
              </div>
            </div>
          ) : mdFile?.md_content ? (
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
              <pre className="text-xs text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
                {mdFile.md_content}
              </pre>
            </div>
          ) : (
            <div className="text-center py-8 bg-stone-50 rounded-lg">
              <FileText className="w-8 h-8 text-stone-200 mx-auto mb-2" />
              <p className="text-xs text-stone-400">No AI context generated yet</p>
              <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={regenerating} className="mt-2 text-xs">
                {regenerating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Generate Now
              </Button>
            </div>
          )}
        </div>

        {/* Version history sidebar */}
        {showVersions && (
          <div className="w-56 flex-shrink-0 border-l border-stone-200 pl-4">
            <p className="text-xs font-semibold text-stone-600 mb-2">Version History</p>
            {versions.length === 0 ? (
              <p className="text-[11px] text-stone-400">No previous versions</p>
            ) : (
              <div className="space-y-1">
                {versions.map(v => (
                  <VersionItem
                    key={v.version_id}
                    version={v}
                    isSelected={selectedVersion?.version === v.version}
                    onSelect={handleViewVersion}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generated timestamp */}
      {mdFile?.generated_at && (
        <p className="text-[10px] text-stone-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Last generated: {new Date(mdFile.generated_at).toLocaleString()}
          {mdFile.generated_by === 'system' ? ' (auto)' : ` by ${mdFile.generated_by}`}
        </p>
      )}
    </div>
  );
}
